import React, { useEffect, useState, useRef } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, addDoc, serverTimestamp, onSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import styles from '../carrier/Notifications.module.css';
import CounterOfferModal from '../../components/shipper/CounterOfferModal';
import { updateLoadRequestStatus, handleBrokerRejection } from '../../services/notificationService';
import { MigrationDataAccessLayer } from '../../services/migrationDataAccessLayer';
import { Dialog } from '@reach/dialog';
import '@reach/dialog/styles.css';
import Logger from '../../services/logger';
import { NOTIFICATION_STATUS } from '../../constants/notificationTypes';

interface Notification {
  id: string;
  type: string;
  senderId: string;
  senderName: string;
  message: string;
  read: boolean;
  createdAt: Date;
  requiresAction?: boolean;
  loadDetails?: {
    pickupLocation: {
      address: string;
      city: string;
      state: string;
    };
    deliveryLocation: {
      address: string;
      city: string;
      state: string;
    };
    rate: number;
    poNumber?: string;
    isMarketplace?: boolean;
  };
  brokerId?: string;
  carrierId?: string;
  poNumber?: string;
}

interface BrokerNotificationsTrayProps {
  onClose: () => void;
}

const BrokerNotificationsTray: React.FC<BrokerNotificationsTrayProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsNotification, setDetailsNotification] = useState<Notification | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const migrationDataAccess = new MigrationDataAccessLayer();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !showDetailsModal &&
        !showCounterOfferModal &&
        trayRef.current &&
        !trayRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, showDetailsModal, showCounterOfferModal]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFailoverAwareAuth(), async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          // Query notifications for broker using brokerId
          const brokerQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('brokerId', '==', user.uid)
          );
          
          // Query notifications for broker using userId (fallback)
          const userQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('userId', '==', user.uid)
          );
          
          let brokerNotifications: Notification[] = [];
          let userNotifications: Notification[] = [];
          let brokerUnsubscribe: (() => void) | null = null;
          let userUnsubscribe: (() => void) | null = null;
          
          const processNotifications = () => {
            // Merge and deduplicate notifications
            const allNotifications = [...brokerNotifications, ...userNotifications];
            const uniqueNotifications = allNotifications.filter((notification, index, self) => 
              index === self.findIndex(n => n.id === notification.id)
            );
            
            // PRODUCTION FIX: Filter out notification types that are handled by dashboard "Action Required" cards
            // These should ONLY appear in the dashboard cards section, NOT in the bell tray
            // Dashboard handles: carrier_decline, make_offer, carrier_counter_offer
            const dashboardHandledTypes = ['carrier_decline', 'make_offer', 'carrier_counter_offer'];
            const bellOnlyNotifications = uniqueNotifications.filter(n => 
              !dashboardHandledTypes.includes(n.type)
            );
            
            // Sort by creation date (newest first)
            bellOnlyNotifications.sort((a, b) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            
            setNotifications(bellOnlyNotifications);
            setLoading(false);
          };
          
          // Set up real-time listeners
          brokerUnsubscribe = onSnapshot(brokerQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            brokerNotifications = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
            })) as Notification[];
            processNotifications();
          });
          
          userUnsubscribe = onSnapshot(userQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            userNotifications = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
            })) as Notification[];
            processNotifications();
          });
          
          // Cleanup function
          return () => {
            if (brokerUnsubscribe) brokerUnsubscribe();
            if (userUnsubscribe) userUnsubscribe();
          };
        } catch (error) {
          Logger.error('Error fetching broker notifications:', error as Error);
          setError('Failed to load notifications');
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      updateDoc(doc(getFailoverAwareDatabase(), 'notifications', notification.id), {
        read: true,
        updatedAt: serverTimestamp()
      });
    }

    // Handle different notification types
    if (notification.type === 'make_offer' && notification.requiresAction) {
      // Marketplace offers with action required - show accept/reject modal
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'carrier_counter_offer' && notification.requiresAction) {
      // Counter offers with action required - show accept/reject modal
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'make_offer' || notification.type === 'carrier_counter_offer') {
      // Marketplace offers or counter offers without action required - show details only
      setDetailsNotification(notification);
      setShowDetailsModal(true);
    }
  };

  const handleAcceptCounterOffer = async () => {
    if (selectedNotification?.id) {
      try {
        Logger.debug('[BrokerNotificationsTray] Accepting counter offer:', selectedNotification);
        
        if (selectedNotification.loadDetails?.poNumber) {
          if (selectedNotification.type === 'make_offer') {
            // Marketplace offer acceptance - use existing flow
            Logger.debug('[BrokerNotificationsTray] Processing make_offer...');
            await updateLoadRequestStatus(selectedNotification.loadDetails.poNumber, NOTIFICATION_STATUS.ACCEPTED);
            
            // Create partnership between broker and carrier
            await createBrokerCarrierPartnership(selectedNotification);
            
          } else if (selectedNotification.type === 'carrier_counter_offer') {
            // Counter offer acceptance - use existing flow
            Logger.debug('[BrokerNotificationsTray] Processing carrier_counter_offer...');
            await updateLoadRequestStatus(selectedNotification.loadDetails.poNumber, NOTIFICATION_STATUS.ACCEPTED, selectedNotification.loadDetails.rate);
            
            // Create partnership between broker and carrier
            await createBrokerCarrierPartnership(selectedNotification);
          }
        }

        // Mark notification as read
        await updateDoc(doc(getFailoverAwareDatabase(), 'notifications', selectedNotification.id), {
          read: true,
          updatedAt: serverTimestamp()
        });

        setShowCounterOfferModal(false);
        setSelectedNotification(null);
        
        // Refresh notifications
        window.location.reload();
        
      } catch (error) {
        Logger.error('[BrokerNotificationsTray] Error accepting offer:', error as Error);
        setError(`Failed to accept offer: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleRejectCounterOffer = async () => {
    if (selectedNotification?.id) {
      try {
        if (selectedNotification.loadDetails?.poNumber) {
          if (selectedNotification.type === 'make_offer') {
            // Marketplace offer rejection - use existing flow
            await updateLoadRequestStatus(selectedNotification.loadDetails.poNumber, NOTIFICATION_STATUS.REJECTED);
          } else if (selectedNotification.type === 'carrier_counter_offer') {
            // Counter offer rejection - use existing flow
            await updateLoadRequestStatus(selectedNotification.loadDetails.poNumber, NOTIFICATION_STATUS.REJECTED);
          }
        }

        // Mark notification as read
        await updateDoc(doc(getFailoverAwareDatabase(), 'notifications', selectedNotification.id), {
          read: true,
          updatedAt: serverTimestamp()
        });

        setShowCounterOfferModal(false);
        setSelectedNotification(null);
        
        // Refresh notifications
        window.location.reload();
        
      } catch (error) {
        Logger.error('Error rejecting offer:', error as Error);
        setError('Failed to reject offer. Please try again.');
      }
    }
  };

  const createBrokerCarrierPartnership = async (notification: Notification) => {
    if (!currentUser || !notification.carrierId) return;

    try {
      // Get broker profile data
      const brokerResult = await migrationDataAccess.getUserProfile(currentUser.uid);
      if (!brokerResult.success || !brokerResult.data) {
        Logger.error('Failed to get broker profile', new Error('Broker profile not found'));
        return;
      }

      const brokerData = brokerResult.data;
      const brokerPartnerData = {
        id: currentUser.uid,
        companyName: brokerData.companyName || brokerData.displayName || 'Broker',
        companyRep: brokerData.displayName || 'Broker Representative',
        phoneNumber: brokerData.phoneNumber || '',
        email: brokerData.email || '',
        state: brokerData.state || '',
        userType: 'broker' as 'broker' | 'carrier' | 'shipper',
        addedAt: new Date()
      };

      // Get carrier profile data
      const carrierResult = await migrationDataAccess.getUserProfile(notification.carrierId);
      if (!carrierResult.success || !carrierResult.data) {
        Logger.error('Failed to get carrier profile', new Error('Carrier profile not found'));
        return;
      }

      const carrierData = carrierResult.data;
      const carrierPartnerData = {
        id: notification.carrierId,
        companyName: carrierData.companyName || carrierData.displayName || 'Carrier',
        companyRep: carrierData.displayName || 'Carrier Representative',
        phoneNumber: carrierData.phoneNumber || '',
        email: carrierData.email || '',
        state: carrierData.state || '',
        userType: 'carrier' as 'broker' | 'carrier' | 'shipper',
        addedAt: new Date()
      };

      // Create partnerships using dual-mode access
      Logger.debug('[BrokerNotificationsTray] Creating partnerships...');
      const addBrokerPartner = await migrationDataAccess.addPartner(currentUser.uid, notification.carrierId, carrierPartnerData);
      const addCarrierPartner = await migrationDataAccess.addPartner(notification.carrierId, currentUser.uid, brokerPartnerData);
      
      Logger.debug('[BrokerNotificationsTray] Partnership creation results:', {
        addBrokerPartner: addBrokerPartner,
        addCarrierPartner: addCarrierPartner
      });
      
      if (!addBrokerPartner.success || !addCarrierPartner.success) {
        Logger.error('[BrokerNotificationsTray] Failed to create partnership:', new Error('Partnership creation failed'), {
          addBrokerPartner: addBrokerPartner,
          addCarrierPartner: addCarrierPartner
        });
        throw new Error(`Partnership creation failed: ${addBrokerPartner.error || addCarrierPartner.error || 'Unknown error'}`);
      }
      
      Logger.debug('[BrokerNotificationsTray] Partnership created successfully!');

      // Create partnership notification for carrier
      await addDoc(collection(getFailoverAwareDatabase(), 'notifications'), {
        type: 'partner_request',
        recipientId: notification.carrierId,
        senderId: currentUser.uid,
        senderName: brokerData.companyName || brokerData.displayName || 'A broker',
        message: `${brokerData.companyName || brokerData.displayName || 'A broker'} has added you as a partner`,
        read: false,
        createdAt: serverTimestamp()
      });

      Logger.debug('Partnership created successfully between broker and carrier');
    } catch (error) {
      Logger.error('Error creating partnership:', error as Error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'make_offer':
        return '💰';
      case 'carrier_counter_offer':
        return '🔄';
      default:
        return '📢';
    }
  };

  const getNotificationTitle = (type: string) => {
    switch (type) {
      case 'make_offer':
        return 'Marketplace Offer';
      case 'carrier_counter_offer':
        return 'Counter Offer';
      default:
        return 'Notification';
    }
  };

  if (loading) {
    return (
      <div className={styles.tray} ref={trayRef}>
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>
        <div className={styles.loading}>Loading notifications...</div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.tray} ref={trayRef}>
        <div className={styles.header}>
          <h3>Notifications</h3>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>
        
        {error && (
          <div className={styles.error}>
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        
        <div className={styles.notificationList}>
          {notifications.length === 0 ? (
            <div className={styles.noNotifications}><p>No notifications</p></div>
          ) : (
            notifications.map((n) => {
              const amount = n.loadDetails?.rate;
              const amountLabel = typeof amount === 'number' ? `$${amount.toLocaleString()}` : undefined;
              const po = n.loadDetails?.poNumber || '—';
              const created = n.createdAt ? new Date(n.createdAt).toLocaleString() : '';

              const badgeClass = n.type === 'make_offer'
                ? styles.typeOffer
                : n.type === 'carrier_counter_offer'
                ? styles.typeCounter
                : (n.type || '').includes('accept')
                ? styles.typeAccepted
                : (n.type || '').includes('reject')
                ? styles.typeRejected
                : styles.typeOffer;

              return (
                <div key={n.id} className={styles.card} onClick={() => handleNotificationClick(n)}>
                  <div className={styles.cardHeader}>
                    <span className={styles.icon}>{getNotificationIcon(n.type)}</span>
                    <span className={`${styles.typeBadge} ${badgeClass}`}>{getNotificationTitle(n.type)}</span>
                  </div>
                  <div className={styles.actions}>
                    {!n.read && <button className={styles.btn} onClick={(e) => { e.stopPropagation(); updateDoc(doc(getFailoverAwareDatabase(),'notifications', n.id), { read: true, updatedAt: serverTimestamp() }); }}>{'Mark read'}</button>}
                    <button className={styles.btnLink} onClick={(e) => { e.stopPropagation(); setDetailsNotification(n); setShowDetailsModal(true); }}>Details</button>
                  </div>
                  <div className={styles.messageRow}>
                    <span>{n.message}</span>
                  </div>
                  <div className={styles.metaRow}>
                    <span>{created}</span>
                    {amountLabel && <span>Amount: <span className={styles.amount}>{amountLabel}</span></span>}
                    <span>PO: {po}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Counter Offer Modal */}
      {showCounterOfferModal && selectedNotification && (
        <CounterOfferModal
          isOpen={showCounterOfferModal}
          onClose={() => {
            setShowCounterOfferModal(false);
            setSelectedNotification(null);
          }}
          currentRate={0} // Marketplace offers don't have a current rate
          counterOffer={selectedNotification.loadDetails?.rate || 0}
          loadDetails={{
            pickupLocation: selectedNotification.loadDetails?.pickupLocation || { address: '', city: '', state: '' },
            deliveryLocation: selectedNotification.loadDetails?.deliveryLocation || { address: '', city: '', state: '' },
            poNumber: selectedNotification.loadDetails?.poNumber || '',
            carrierName: selectedNotification.senderName || 'Unknown Carrier'
          }}
          onAccept={handleAcceptCounterOffer}
          onReject={handleRejectCounterOffer}
        />
      )}

      {/* Details Modal */}
      {showDetailsModal && detailsNotification && (
        <Dialog
          isOpen={showDetailsModal}
          onDismiss={() => {
            setShowDetailsModal(false);
            setDetailsNotification(null);
          }}
        >
          <div className={styles.detailsModal}>
            <h3>Offer Details</h3>
            <div className={styles.detailsContent}>
              <p><strong>From:</strong> {detailsNotification.senderName}</p>
              <p><strong>Message:</strong> {detailsNotification.message}</p>
              {detailsNotification.loadDetails?.rate && (
                <p><strong>Rate:</strong> ${detailsNotification.loadDetails.rate.toLocaleString()}</p>
              )}
              {detailsNotification.loadDetails?.poNumber && (
                <p><strong>PO Number:</strong> {detailsNotification.loadDetails.poNumber}</p>
              )}
              <p><strong>Time:</strong> {new Date(detailsNotification.createdAt).toLocaleString()}</p>
            </div>
            <button
              onClick={() => {
                setShowDetailsModal(false);
                setDetailsNotification(null);
              }}
              className={styles.closeButton}
            >
              Close
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default BrokerNotificationsTray;
